import { cn } from "@aircall/ds";
import {
  ComponentPropsWithoutRef,
  UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

function isScrollAtBottom<T extends HTMLElement>(element: T): boolean {
  return element.scrollHeight <= element.scrollTop + element.clientHeight;
}

function isScrollAtTop<T extends HTMLElement>(element: T): boolean {
  return element.scrollTop === 0;
}

type ScrollShadowContainerProps = ComponentPropsWithoutRef<"div">;

export function ScrollShadowContainer({
  children,
  className,
  style,
  onScroll,
  ...divProps
}: ScrollShadowContainerProps) {
  const localRef = useRef<HTMLDivElement>(null);
  const [hasBottomShadow, setBottomShadow] = useState<boolean>(false);
  const [hasTopShadow, setTopShadow] = useState<boolean>(false);

  const setScrollShadow = useCallback((el: HTMLDivElement) => {
    const atBottom = isScrollAtBottom<HTMLDivElement>(el);
    const atTop = isScrollAtTop<HTMLDivElement>(el);

    setBottomShadow(!atBottom);
    setTopShadow(!atTop);
  }, []);

  useEffect(() => {
    if (localRef.current) {
      setScrollShadow(localRef.current);
    }
  }, [setScrollShadow]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      setScrollShadow(event.currentTarget);
      onScroll?.(event);
    },
    [setScrollShadow, onScroll]
  );

  const shadows: string[] = [
    ...(hasTopShadow ? [`inset 0 19px 20px -31px grey`] : []),
    ...(hasBottomShadow ? [`inset 0 -19px 20px -31px grey`] : []),
  ];

  const boxShadow = shadows.length > 0 ? shadows.join(",") : undefined;

  return (
    <div
      {...divProps}
      ref={localRef}
      onScroll={handleScroll}
      className={cn(className)}
      style={{
        boxShadow,
        transition: "500ms box-shadow",
        ...style,
      }}
    >
      {children}
    </div>
  );
}
